import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../../../lib/sprache";
import { useAuslassungTitel } from "../../../lib/auslassung";
import "./einstellungen.css";

/**
 * Einklappbarer Abschnitt der Einstellungen (D-1 Gruppe C, Inhaber 21.10.2026: „kompakt, alles in einem Blick“). Kopf mit
 * Titel und Zusammenfassung des gespeicherten Stands in einer Zeile; ein Klick klappt den Inhalt auf. Ein Abschnitt mit
 * ungespeicherten Änderungen oder einem Fehler bleibt offen und zeigt das im Kopf an:
 *  - geändert: ausdrücklich über `geaendert`; ohne Angabe gilt jede Eingabe im Abschnitt als Änderung, bis ein Formular darin
 *    ohne Fehler abgeschickt wurde oder sich die Zusammenfassung (gespeicherter Stand) ändert,
 *  - Fehler: über `fehler` oder sobald im Inhalt eine Fehlermeldung (.tk-error, role="alert") oder ein ungültiges Feld steht.
 * Die Felder bleiben auch eingeklappt im Formular (nur ausgeblendet), Speichern über einen gemeinsamen Knopf funktioniert daher.
 */
/**
 * IA-1 (21.10.2026): Auf den Einzelseiten von „Alle Einstellungen“ hat jede Seite EIN Thema. Dort steht ein Abschnitt immer
 * offen als normales Panel mit Überschrift (kein Klappkopf); die Seiten setzen dafür <KlappOffen>.
 */
const KlappOffenKontext = createContext(false);
export function KlappOffen({ children }: { children: ReactNode }) {
  return <KlappOffenKontext.Provider value={true}>{children}</KlappOffenKontext.Provider>;
}

/** Aufgeklappte Abschnitte (nach Titel) für diese Sitzung: Speichern zeichnet ein Formular neu, der Abschnitt bleibt offen */
const offeneAbschnitte = new Set<string>();

export function Klappabschnitt(props: Parameters<typeof KlappZu>[0]) {
  const immerOffen = useContext(KlappOffenKontext);
  if (immerOffen) return <OffenerAbschnitt id={props.id} titel={props.titel}>{props.children}</OffenerAbschnitt>;
  return <KlappZu {...props} />;
}

/** Abschnitt ohne Klappkopf (Einzelseite): Panel mit Überschrift, darunter der Inhalt. */
function OffenerAbschnitt({ id, titel, children }: { id?: string | undefined; titel: ReactNode; children: ReactNode }) {
  const eigeneId = useId();
  const kopfId = id ?? `${eigeneId}-kopf`;
  return (
    <section className="panel stack tk-klapp-offen" aria-labelledby={kopfId}>
      <h2 id={kopfId}>{titel}</h2>
      {children}
    </section>
  );
}

function KlappZu({ id, titel, zusammenfassung, geaendert, fehler, offen: anfangsOffen = false, children }: {
  /** id der Überschrift (für aria-labelledby im Inhalt) */
  id?: string | undefined;
  titel: ReactNode;
  zusammenfassung?: string;
  geaendert?: boolean;
  fehler?: boolean;
  /** beim ersten Zeichnen aufgeklappt (z. B. fehlende Pflichtangabe) */
  offen?: boolean;
  children: ReactNode;
}) {
  const eigeneId = useId();
  const kopfId = id ?? `${eigeneId}-kopf`;
  const inhaltId = `${eigeneId}-inhalt`;
  const merk = typeof titel === "string" ? titel : null;
  const [offen, setOffenRoh] = useState(() => anfangsOffen || (merk !== null && offeneAbschnitte.has(merk)));
  const setOffen = (wert: boolean | ((o: boolean) => boolean)) => setOffenRoh((o) => {
    const neu = typeof wert === "function" ? wert(o) : wert;
    if (merk !== null) { if (neu) offeneAbschnitte.add(merk); else offeneAbschnitte.delete(merk); }
    return neu;
  });
  const [eingabe, setEingabe] = useState(false);
  const [domFehler, setDomFehler] = useState(false);
  const inhalt = useRef<HTMLDivElement>(null);
  const kopf = useAuslassungTitel<HTMLHeadingElement>();
  const gesendet = useRef<number | null>(null);

  const istGeaendert = geaendert ?? eingabe;
  const hatFehler = !!fehler || domFehler;
  const festgehalten = istGeaendert || hatFehler;

  // Gespeicherter Stand hat sich geändert: die Eingaben sind übernommen
  useEffect(() => { setEingabe(false); }, [zusammenfassung]);
  // Fehler oder Änderung: aufklappen (und offen halten)
  useEffect(() => { if (festgehalten) setOffen(true); }, [festgehalten]);
  // Fehler im Inhalt erkennen (auch in verschachtelten Formularen), bei jeder Änderung des Inhalts
  useLayoutEffect(() => {
    const el = inhalt.current;
    if (!el) return;
    const pruefe = () => setDomFehler(!!el.querySelector(".tk-error, [role=alert], [aria-invalid=true]"));
    pruefe();
    if (typeof MutationObserver === "undefined") return;
    const beobachter = new MutationObserver(pruefe);
    beobachter.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-invalid", "class", "role"] });
    return () => beobachter.disconnect();
  }, []);
  useEffect(() => () => { if (gesendet.current) clearTimeout(gesendet.current); }, []);

  const umschalten = () => setOffen((o) => (o && festgehalten ? true : !o));
  return (
    <section className={`panel tk-klapp${offen ? " offen" : ""}`} aria-labelledby={kopfId}>
      <h2 id={kopfId} ref={kopf} className="tk-klapp-kopf">
        <button type="button" aria-expanded={offen} aria-controls={inhaltId} onClick={umschalten}
          title={offen && festgehalten ? t("Bleibt offen, bis die Änderungen gespeichert oder die Fehler behoben sind", "Stays open until the changes are saved or the errors are fixed") : undefined}>
          <span className="tk-klapp-titel">{titel}</span>
          {zusammenfassung && <span className="tk-klapp-zus tk-ell">{zusammenfassung}</span>}
          {hatFehler ? <span className="pill error"><span className="pill-zeichen">!</span> {t("Fehler", "Error")}</span>
            : istGeaendert ? <span className="pill warning">{t("Nicht gespeichert", "Not saved")}</span> : null}
          <span className="tk-klapp-pfeil" aria-hidden="true">{offen ? "▴" : "▾"}</span>
        </button>
      </h2>
      <div id={inhaltId} ref={inhalt} className="stack tk-klapp-inhalt" hidden={!offen}
        onInput={() => setEingabe(true)} onChange={() => setEingabe(true)}
        onSubmit={() => {
          // Abgeschickt: ohne Fehlermeldung kurz danach gilt der Stand als gespeichert
          if (gesendet.current) clearTimeout(gesendet.current);
          gesendet.current = window.setTimeout(() => {
            if (!inhalt.current?.querySelector(".tk-error, [role=alert], [aria-invalid=true]")) setEingabe(false);
          }, 1500);
        }}>
        {children}
      </div>
    </section>
  );
}
